import React from 'react';
import ErrorPageImage from '../../assets/error_page_image.svg';

export interface ErrorStateProps {
  title?: string;
  message?: string;
  onRetry?: () => void;
  retryLabel?: string;
  showRetryButton?: boolean;
  type?: 'not-found' | 'server-error' | 'network-error' | 'timeout' | 'bad-gateway';
  className?: string;
}

const ErrorState: React.FC<ErrorStateProps> = ({
  title,
  message,
  onRetry,
  retryLabel = 'Try Again',
  showRetryButton = true,
  type = 'not-found',
  className = '',
}) => {
  // Default messages based on error type
  const getDefaultContent = () => {
    switch (type) {
      case 'not-found':
        return {
          title: title || 'Not Found',
          message: message || 'The requested resource could not be found.',
          showImage: false,
        };
      case 'network-error':
        return {
          title: title || 'Connection Problem',
          message: message || 'Unable to connect to the server. Please check your internet connection.',
          showImage: true,
        };
      case 'timeout':
        return {
          title: title || 'Request Timeout',
          message: message || 'The server took too long to respond. Please try again.',
          showImage: true,
        };
      case 'bad-gateway':
        return {
          title: title || 'Bad Gateway',
          message: message || 'The server encountered a temporary error and could not complete your request. Please try again in a moment.',
          showImage: true,
        };
      case 'server-error':
      default:
        return {
          title: title || 'Something Went Wrong',
          message: message || 'An unexpected error occurred. Please try again later.',
          showImage: true,
        };
    }
  };

  const content = getDefaultContent();

  return (
    <div className={`flex flex-col items-center justify-center min-h-full py-4 px-4 ${className}`}>
      {/* Error Image - only for server/network errors */}
      {content.showImage && (
        <div className="w-full mb-8 flex justify-center">
          <img
            src={ErrorPageImage}
            alt="Error illustration"
            className="w-full max-w-lg sm:max-w-xl lg:max-w-2xl h-auto object-contain"
          />
        </div>
      )}

      {/* Error Icon - for not-found errors */}
      {!content.showImage && (
        <div className="mb-6">
          <svg
            className="w-24 h-24 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
      )}

      {/* Error Message */}
      <div className="text-center max-w-md">
        <h2 className="text-2xl font-semibold text-gray-900 mb-3">
          {content.title}
        </h2>
        <p className="text-gray-600 text-sm mb-6">
          {getDefaultContent().message}
        </p>

        {/* Retry Button */}
        {showRetryButton && onRetry && (
          <button
            onClick={onRetry}
            className="inline-flex items-center justify-center px-6 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            {retryLabel}
          </button>
        )}
      </div>
    </div>
  );
};

export default ErrorState;
